import React, { useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

const CreateGameList = () => {
    const { isAuthenticated } = useContext(AuthContext);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        isPublic: true,
        tags: ''
    });
    const [addedGames, setAddedGames] = useState([]);
    
    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [showResults, setShowResults] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;

        setSearching(true);
        try {
            const response = await axios.get(`/api/games/search-rawg?q=${encodeURIComponent(searchQuery)}`);
            const rawgResults = response.data.results || [];
            if (rawgResults.length > 0) {
                setSearchResults(rawgResults);
            } else {
                const dbRes = await axios.get(`/api/games/search?q=${encodeURIComponent(searchQuery)}`);
                setSearchResults(dbRes.data.games || []);
            }
            setShowResults(true);
        } catch (err) {
            try {
                const dbRes = await axios.get(`/api/games/search?q=${encodeURIComponent(searchQuery)}`);
                setSearchResults(dbRes.data.games || []);
                setShowResults(true);
            } catch (err2) {
                console.error('Search error:', err2);
            }
        } finally {
            setSearching(false);
        }
    };

    const addGame = async (rawgItem) => {
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
            const res = await axios.post('/api/games', { name: rawgItem.name }, { headers });
            const dbGame = res.data.game || res.data.data || res.data; // controller returns {success, game}
            if (!dbGame || !dbGame._id) return;
            if (addedGames.find(g => String(g.game._id) === String(dbGame._id))) return;
            setAddedGames([...addedGames, { game: dbGame, note: '' }]);
            setSearchQuery('');
            setShowResults(false);
        } catch (err) {
            console.error('Add game error:', err);
        }
    };

    const removeGame = (index) => {
        const newGames = [...addedGames];
        newGames.splice(index, 1);
        setAddedGames(newGames);
    };

    const updateGameNote = (index, note) => {
        const newGames = [...addedGames];
        newGames[index].note = note;
        setAddedGames(newGames);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title.trim()) {
            setError('Title is required');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const listData = {
                title: formData.title,
                description: formData.description,
                isPublic: formData.isPublic,
                tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
                games: addedGames.map(item => ({
                    game: item.game._id,
                    note: item.note
                }))
            };

            const response = await axios.post('/api/game-lists', listData, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.data.success) {
                navigate(`/lists/${response.data.data._id}`);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create list');
            setSubmitting(false);
        }
    };

    if (!isAuthenticated) {
        return (
            <div className="container" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-primary)' }}>
                <h2>Please log in to create a list</h2>
                <button onClick={() => navigate('/login')} className="btn btn-primary">Login</button>
            </div>
        );
    }

    return (
        <div className="container" style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
            <h1 style={{ color: 'var(--text-primary)', marginBottom: '20px' }}>Create New List</h1>
            
            {error && <div style={{ color: 'var(--error-text)', marginBottom: '20px', padding: '10px', background: 'var(--error-bg)', borderRadius: '8px' }}>{error}</div>}

            <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', color: 'var(--text-primary)' }}>List Title *</label>
                    <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--card-bg)', color: 'var(--text-primary)' }}
                        placeholder="e.g. Top 10 Horror Games"
                        required
                    />
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', color: 'var(--text-primary)' }}>Description</label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--card-bg)', color: 'var(--text-primary)', minHeight: '100px' }}
                        placeholder="Describe your list..."
                    />
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', color: 'var(--text-primary)' }}>Tags (comma separated)</label>
                    <input
                        type="text"
                        value={formData.tags}
                        onChange={(e) => setFormData({...formData, tags: e.target.value})}
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--card-bg)', color: 'var(--text-primary)' }}
                        placeholder="horror, indie, 2024"
                    />
                </div>

                <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
                    <input
                        type="checkbox"
                        checked={formData.isPublic}
                        onChange={(e) => setFormData({...formData, isPublic: e.target.checked})}
                        style={{ marginRight: '10px', width: '20px', height: '20px' }}
                    />
                    <label style={{ color: 'var(--text-primary)' }}>Public List (Visible to everyone)</label>
                </div>

                <div style={{ marginBottom: '30px', borderTop: '1px solid var(--border-color)', paddingTop: '20px' }}>
                    <h3 style={{ color: 'var(--text-primary)', marginBottom: '15px' }}>Add Games</h3>
                    
                    <div style={{ position: 'relative', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--card-bg)', color: 'var(--text-primary)' }}
                                placeholder="Search for games..."
                            />
                            <button 
                                type="button" 
                                onClick={handleSearch}
                                style={{ padding: '0 20px', background: 'var(--primary-color)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                            >
                                {searching ? 'Searching...' : 'Search'}
                            </button>
                        </div>

                        {showResults && searchResults.length > 0 && (
                            <div style={{ 
                                position: 'absolute', 
                                top: '100%', 
                                left: 0, 
                                right: 0, 
                                background: 'var(--card-bg)', 
                                border: '1px solid var(--border-color)', 
                                borderRadius: '8px', 
                                zIndex: 10,
                                maxHeight: '300px',
                                overflowY: 'auto',
                                marginTop: '5px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                            }}>
                                {searchResults.map(game => (
                                    <div 
                                        key={game.rawgId} 
                                        onClick={() => addGame(game)}
                                        style={{ 
                                            padding: '10px', 
                                            borderBottom: '1px solid var(--border-color)', 
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                        className="search-result-hover"
                                    >
                                        <img 
                                            src={game.coverImage || '/placeholder-game.jpg'} 
                                            alt={game.name} 
                                            style={{ width: '40px', height: '60px', objectFit: 'cover', marginRight: '10px', borderRadius: '4px' }}
                                        />
                                        <div>
                                            <div style={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>{game.name}</div>
                                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{game.released || ''}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        {addedGames.map((item, index) => (
                            <div key={index} style={{ 
                                background: 'var(--card-bg)', 
                                padding: '15px', 
                                borderRadius: '8px', 
                                border: '1px solid var(--border-color)',
                                display: 'flex',
                                gap: '15px'
                            }}>
                                <div style={{ 
                                    width: '30px', 
                                    height: '30px', 
                                    background: 'var(--primary-color)', 
                                    borderRadius: '50%', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    flexShrink: 0
                                }}>
                                    {index + 1}
                                </div>
                                <img 
                                    src={item.game.coverImage || '/placeholder-game.jpg'} 
                                    alt={item.game.name} 
                                    style={{ width: '60px', height: '90px', objectFit: 'cover', borderRadius: '4px' }} 
                                />
                                <div style={{ flex: 1 }}>
                                    <h4 style={{ margin: '0 0 5px 0', color: 'var(--text-primary)' }}>{item.game.name}</h4>
                                    <input
                                        type="text"
                                        value={item.note}
                                        onChange={(e) => updateGameNote(index, e.target.value)}
                                        placeholder="Add a note (optional)..."
                                        style={{ 
                                            width: '100%', 
                                            padding: '8px', 
                                            borderRadius: '4px', 
                                            border: '1px solid var(--border-color)', 
                                            background: 'var(--bg-secondary)', 
                                            color: 'var(--text-primary)',
                                            fontSize: '0.9rem'
                                        }}
                                    />
                                </div>
                                <button 
                                    type="button" 
                                    onClick={() => removeGame(index)}
                                    style={{ 
                                        background: 'transparent', 
                                        border: 'none', 
                                        color: 'var(--error-text)', 
                                        cursor: 'pointer',
                                        height: 'fit-content'
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                        
                        {addedGames.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)', border: '1px dashed var(--border-color)', borderRadius: '8px' }}>
                                No games added yet. Search above to add games to your list.
                            </div>
                        )}
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button 
                        type="button" 
                        onClick={() => navigate('/discover-lists')}
                        style={{ padding: '12px 24px', background: 'transparent', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '8px', cursor: 'pointer' }}
                    >
                        Cancel
                    </button>
                    <button 
                        type="submit" 
                        disabled={submitting || addedGames.length === 0}
                        style={{ 
                            padding: '12px 24px', 
                            background: 'var(--primary-color)', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '8px', 
                            cursor: (submitting || addedGames.length === 0) ? 'not-allowed' : 'pointer',
                            opacity: (submitting || addedGames.length === 0) ? 0.7 : 1
                        }}
                    >
                        {submitting ? 'Creating...' : 'Create List'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreateGameList;
